import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle, Phone, Mail, MapPin, Calendar, User, Wrench, Building, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicLeadForm() {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);
  
  // Form data state
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address_line: '',
    city: '',
    state: '',
    pincode: '',
    service_required_date: '',
    service_required: '',
    additional_notes: ''
  });

  // Validation errors
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchFormAndServices();
  }, [formId]);

  const fetchFormAndServices = async () => {
    try {
      const formRes = await axios.get(`${BACKEND_URL}/api/lead-forms/${formId}`);
      
      const formData = formRes.data;
      const formName = formData.form_name || formData.name || 'Service Request Form';
      
      setForm({
        ...formData,
        form_name: formName
      });
    } catch (error) {
      console.error('Error fetching form:', error);
      toast.error('Form not found');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) newErrors.phone = 'Enter a valid 10-digit phone number';
    if (!formData.address_line.trim()) newErrors.address_line = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.pincode.trim()) newErrors.pincode = 'Pin code is required';
    if (!formData.service_required_date) newErrors.service_required_date = 'Service date is required';
    if (!formData.service_required.trim()) newErrors.service_required = 'Please enter the service required';
    if (!consent) newErrors.consent = 'Please provide your consent';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/leads/public-submit`, {
        form_id: formId,
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        property_address: {
          address_line: formData.address_line,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode
        },
        service_required_date: formData.service_required_date,
        services_required: [formData.service_required],
        additional_notes: formData.additional_notes,
        consent_given: consent
      });
      
      setSubmitted(true);
      toast.success('Thank you! Your service request has been submitted.');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <Card className="max-w-md shadow-xl">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">❌</span>
            </div>
            <p className="text-xl font-semibold text-gray-900 mb-2">Form Not Found</p>
            <p className="text-gray-500">This form may have been removed or the link is invalid.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        {/* Header */}
        <div className="bg-white border-b-4 border-teal-600 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-5">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_a43a1576-2447-420f-b8ba-eeae8c53cffe/artifacts/a7oiuv0k_Black%20-%20With%20ROC%20number.png"
                alt="Redefyne Logo"
                className="w-20 h-20 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Redefyne- Interior and Exterior</h1>
                <p className="text-gray-500 text-sm mt-1">
                  2301 South Stearman Drive | Chandler, Arizona 85286
                </p>
                <p className="text-gray-500 text-sm">
                  602-715-1313 | support@redefyneaz.com
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center py-16 px-4">
          <Card className="max-w-lg w-full shadow-xl border-0">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                Thank You!
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Your service request has been submitted successfully. Our team will contact you shortly.
              </p>
              <div className="bg-teal-50 rounded-lg p-4 text-left">
                <p className="text-sm text-teal-800 font-medium mb-2">What happens next?</p>
                <ul className="text-sm text-teal-700 space-y-1">
                  <li>• We'll review your request within 24 hours</li>
                  <li>• A team member will reach out to discuss your needs</li>
                  <li>• We'll provide a free estimate for your project</li>
                </ul>
              </div>
              <p className="text-gray-400 text-sm mt-6">
                You can close this window now.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white py-4 text-center text-sm">
          <p>© 2025 Redefyne Interior & Exterior | 2301 S Stearman Dr, Chandler, AZ 85286 | 602-715-1313</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 pb-24">
      {/* Header */}
      <div className="bg-white border-b-4 border-teal-600 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-5">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_a43a1576-2447-420f-b8ba-eeae8c53cffe/artifacts/a7oiuv0k_Black%20-%20With%20ROC%20number.png"
              alt="Redefyne Logo"
              className="w-20 h-20 object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Redefyne- Interior and Exterior</h1>
              <p className="text-gray-500 text-sm mt-1">
                2301 South Stearman Drive | Chandler, Arizona 85286
              </p>
              <p className="text-gray-500 text-sm">
                602-715-1313 | support@redefyneaz.com | Redefyneaz.com
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Form Title Card */}
        <Card className="shadow-xl border-0 overflow-hidden">
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5 text-white">
            <h2 className="text-2xl font-bold">{form.form_name}</h2>
            <p className="text-teal-100 text-sm mt-1">Please fill in your details below and we'll get back to you shortly.</p>
          </div>
          
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Section 1: Personal Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
                  <User className="w-5 h-5 text-teal-600" />
                  Personal Information
                </h3>
                
                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="full_name"
                    data-testid="lead-full-name"
                    value={formData.full_name}
                    onChange={(e) => handleInputChange('full_name', e.target.value)}
                    placeholder="Enter your full name"
                    className={`border-gray-200 focus:border-teal-500 focus:ring-teal-500 ${errors.full_name ? 'border-red-500' : ''}`}
                  />
                  {errors.full_name && <p className="text-red-500 text-xs">{errors.full_name}</p>}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    Email ID <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="lead-email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter your email address"
                    className={`border-gray-200 focus:border-teal-500 focus:ring-teal-500 ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    Phone Number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="lead-phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="Enter 10-digit phone number"
                    className={`border-gray-200 focus:border-teal-500 focus:ring-teal-500 ${errors.phone ? 'border-red-500' : ''}`}
                  />
                  {errors.phone && <p className="text-red-500 text-xs">{errors.phone}</p>}
                </div>
              </div>

              {/* Section 2: Property Address */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
                  <Building className="w-5 h-5 text-teal-600" />
                  Property Address
                </h3>

                {/* Address Line */}
                <div className="space-y-2">
                  <Label htmlFor="address_line" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    Address Line <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="address_line"
                    data-testid="lead-address-line"
                    value={formData.address_line}
                    onChange={(e) => handleInputChange('address_line', e.target.value)}
                    placeholder="Street address, apartment, suite, etc."
                    className={`border-gray-200 focus:border-teal-500 focus:ring-teal-500 ${errors.address_line ? 'border-red-500' : ''}`}
                  />
                  {errors.address_line && <p className="text-red-500 text-xs">{errors.address_line}</p>}
                </div>

                {/* City and State */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-sm font-medium text-gray-700">
                      City <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="city"
                      data-testid="lead-city"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="City"
                      className={`border-gray-200 focus:border-teal-500 focus:ring-teal-500 ${errors.city ? 'border-red-500' : ''}`}
                    />
                    {errors.city && <p className="text-red-500 text-xs">{errors.city}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-sm font-medium text-gray-700">
                      State <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="state"
                      data-testid="lead-state"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      placeholder="State"
                      className={`border-gray-200 focus:border-teal-500 focus:ring-teal-500 ${errors.state ? 'border-red-500' : ''}`}
                    />
                    {errors.state && <p className="text-red-500 text-xs">{errors.state}</p>}
                  </div>
                </div>

                {/* Pin Code */}
                <div className="space-y-2">
                  <Label htmlFor="pincode" className="text-sm font-medium text-gray-700">
                    Pin Code <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="pincode"
                    data-testid="lead-pincode"
                    value={formData.pincode}
                    onChange={(e) => handleInputChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter pin code"
                    className={`border-gray-200 focus:border-teal-500 focus:ring-teal-500 ${errors.pincode ? 'border-red-500' : ''}`}
                  />
                  {errors.pincode && <p className="text-red-500 text-xs">{errors.pincode}</p>}
                </div>
              </div>

              {/* Section 3: Service Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
                  <Wrench className="w-5 h-5 text-teal-600" />
                  Service Details
                </h3>

                {/* Service Required Date */}
                <div className="space-y-2">
                  <Label htmlFor="service_date" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    Service Required Date <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="service_date"
                    type="date"
                    data-testid="lead-service-date"
                    value={formData.service_required_date}
                    onChange={(e) => handleInputChange('service_required_date', e.target.value)}
                    min={getMinDate()}
                    className={`border-gray-200 focus:border-teal-500 focus:ring-teal-500 ${errors.service_required_date ? 'border-red-500' : ''}`}
                  />
                  {errors.service_required_date && <p className="text-red-500 text-xs">{errors.service_required_date}</p>}
                </div>

                {/* Services Required (Multi-select) */}
                <div className="space-y-2">
                  <Label htmlFor="service_required" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-gray-400" />
                    Service Required <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="service_required"
                    data-testid="lead-service-required"
                    value={formData.service_required}
                    onChange={(e) => handleInputChange('service_required', e.target.value)}
                    placeholder="e.g., Plumbing repair, Painting, AC installation..."
                    className={`border-gray-200 focus:border-teal-500 focus:ring-teal-500 ${errors.service_required ? 'border-red-500' : ''}`}
                  />
                  {errors.service_required && <p className="text-red-500 text-xs">{errors.service_required}</p>}
                </div>

                {/* Additional Notes */}
                <div className="space-y-2">
                  <Label htmlFor="additional_notes" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    Additional Notes (Optional)
                  </Label>
                  <Textarea
                    id="additional_notes"
                    data-testid="lead-additional-notes"
                    value={formData.additional_notes}
                    onChange={(e) => handleInputChange('additional_notes', e.target.value)}
                    placeholder="Please provide any additional details about your service request..."
                    className="min-h-[100px] resize-none border-gray-200 focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>
              </div>

              {/* Consent Checkbox */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="consent"
                    data-testid="lead-consent"
                    checked={consent}
                    onCheckedChange={setConsent}
                    className="mt-1 data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600"
                  />
                  <Label 
                    htmlFor="consent" 
                    className="text-sm text-gray-600 leading-relaxed cursor-pointer"
                  >
                    I consent & authorize Redefyne to contact me via Email, WhatsApp, and phone calls for service updates, transaction notifications, promotional communications, and important information related to my request.
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                </div>
                {errors.consent && <p className="text-red-500 text-xs mt-2">{errors.consent}</p>}
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold shadow-lg"
                data-testid="submit-lead-form"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  'Submit Service Request'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Trust Badges */}
        <div className="mt-6 grid grid-cols-3 gap-4 text-center">
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-2xl mb-1">✅</div>
            <p className="text-xs text-gray-600">Licensed & Insured</p>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-2xl mb-1">⭐</div>
            <p className="text-xs text-gray-600">5-Star Rated</p>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-2xl mb-1">🏠</div>
            <p className="text-xs text-gray-600">Free Estimates</p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>© 2025 Redefyne Interior & Exterior. All rights reserved.</p>
          <p className="mt-1">ROC# 338264 | Chandler, Arizona</p>
        </div>
      </div>
    </div>
  );
}
